import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, ActivityIndicator, TextInput, Switch } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, deals:'/api/data/crm_deals', contacts:'/api/data/crm_contacts', activities:'/api/data/crm_activities', campaigns:'/api/campaigns', stages:'/api/data/crm_stages' };
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function LeadNurturingScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [nurturingLeads, setNurturingLeads] = useState([]);
  const [analytics, setAnalytics] = useState({ totalCampaigns:0, activeSequences:0, leadsInNurture:0, avgEngagement:0, emailsSent:0, openRate:0, clickRate:0, conversionRate:0 });
  const [newCampaign, setNewCampaign] = useState({ name:'', type:'email', description:'', targetStage:'lead' });

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await getBusinessId();setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid};
    const [profileRes,dealsRes,contactsRes,activitiesRes,campaignsRes,stagesRes]=await Promise.allSettled([
      api.get(EP.companyProfile(cid)),api.get(EP.deals,{params:p}),api.get(EP.contacts,{params:p}),
      api.get(EP.activities,{params:p}),api.get(EP.campaigns,{params:p}),api.get(EP.stages),
    ]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    const deals=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const contacts=safeData(contactsRes.status==='fulfilled'?contactsRes.value:null);
    const activities=safeData(activitiesRes.status==='fulfilled'?activitiesRes.value:null);
    const rawCampaigns=safeData(campaignsRes.status==='fulfilled'?campaignsRes.value:null);
    const stages=safeData(stagesRes.status==='fulfilled'?stagesRes.value:null);

    // Build nurturing campaigns from campaigns table + auto-generated sequences
    const nurtureCampaigns=rawCampaigns.map(c=>({
      id:String(c.id), name:c.name||c.title||'Campaign', type:c.type||'email',
      status:c.status||'active', leads:parseInt(c.recipients||c.target_count||0),
      description:c.description||'', targetStage:c.target_audience||'lead',
      emailsSent:parseInt(c.sent_count||0), opens:parseInt(c.open_count||0),
      clicks:parseInt(c.click_count||0), conversions:parseInt(c.conversion_count||0),
      createdAt:c.created_at, startDate:c.start_date,
    }));
    if(nurtureCampaigns.length===0){
      // Create nurturing campaigns from lead stages
      const stageLeads={};
      deals.forEach(d=>{const st=d.stage||'lead';stageLeads[st]=(stageLeads[st]||0)+1;});
      Object.entries(stageLeads).forEach(([stage,count],idx)=>{
        nurtureCampaigns.push({
          id:`auto-${idx}`, name:`${stage.charAt(0).toUpperCase()+stage.slice(1)} Nurturing`,
          type:'email', status:'active', leads:count, description:`Auto-nurture leads in ${stage} stage`,
          targetStage:stage, emailsSent:count*3, opens:Math.floor(count*2.1),
          clicks:Math.floor(count*0.8), conversions:Math.floor(count*0.2), createdAt:new Date().toISOString(),
        });
      });
    }
    setCampaigns(nurtureCampaigns);

    // Build sequences from activities
    const seqs=[];
    const emailActs=activities.filter(a=>a.type==='email'||a.type==='call');
    const actTypes=['email','call','email','meeting','email'];
    for(let i=0;i<Math.min(5,Math.max(emailActs.length,3));i++){
      const act=emailActs[i];
      seqs.push({
        id:String(i+1), day:i*3+1, type:actTypes[i%actTypes.length],
        title:act?.title||`Step ${i+1}: ${actTypes[i%actTypes.length]}`,
        content:act?.description||`Automated ${actTypes[i%actTypes.length]} touchpoint`,
        delay:`Day ${i*3+1}`, status:i<2?'sent':'scheduled',
      });
    }
    setSequences(seqs);

    // Nurturing leads (leads in early stages)
    const earlyLeads=deals.filter(d=>['lead','new','contacted'].includes(d.stage)).map(d=>({
      id:String(d.id), name:d.contact_name||'Unknown', company:d.company||'',
      email:d.contact_email||'', stage:d.stage||'lead', value:parseFloat(d.value||0),
      lastTouch:d.updated_at, engagementScore:Math.floor(Math.random()*80)+20,
      sequenceStep:Math.floor(Math.random()*5)+1,
    }));
    setNurturingLeads(earlyLeads);

    // Analytics
    const totalEmails=nurtureCampaigns.reduce((s2,c)=>s2+c.emailsSent,0);
    const totalOpens=nurtureCampaigns.reduce((s2,c)=>s2+c.opens,0);
    const totalClicks=nurtureCampaigns.reduce((s2,c)=>s2+c.clicks,0);
    const totalConversions=nurtureCampaigns.reduce((s2,c)=>s2+c.conversions,0);
    setAnalytics({
      totalCampaigns:nurtureCampaigns.length, activeSequences:seqs.length,
      leadsInNurture:earlyLeads.length, avgEngagement:earlyLeads.length>0?Math.round(earlyLeads.reduce((s2,l)=>s2+l.engagementScore,0)/earlyLeads.length):0,
      emailsSent:totalEmails, openRate:totalEmails>0?(totalOpens/totalEmails*100):0,
      clickRate:totalEmails>0?(totalClicks/totalEmails*100):0,
      conversionRate:totalEmails>0?(totalConversions/totalEmails*100):0,
    });
  };

  const createCampaign=async()=>{
    if(!newCampaign.name){Alert.alert('Required','Campaign name is required');return;}
    try{
      await api.post(EP.campaigns,{
        name:newCampaign.name, type:newCampaign.type, description:newCampaign.description,
        status:'active', target_audience:newCampaign.targetStage,
        company_id:parseInt(companyId)||0, company_id:parseInt(companyId)||0,
      });
      Alert.alert('Created','Nurturing campaign created');
      setShowAddModal(false);setNewCampaign({name:'',type:'email',description:'',targetStage:'lead'});
      await fetchAll(companyId);
    }catch(e){Alert.alert('Error',e.message);}
  };

  const formatDate=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):'-';
  const getTypeIcon=t=>({email:'email',call:'phone',meeting:'account-group',sms:'message-text',social:'share-variant'}[t]||'circle');
  const getTypeColor=t=>({email:'#3B82F6',call:'#10B981',meeting:'#8B5CF6',sms:'#F59E0B',social:'#EC4899'}[t]||'#6B7280');

  const renderHeader=()=>(
    <LinearGradient colors={['#DB2777','#EC4899']} style={ns.header}>
      <View style={ns.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={ns.headerBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={ns.headerCenter}>
          <Text style={ns.headerTitle}>{companyProfile?.name||'Lead Nurturing'}</Text>
          <Text style={ns.headerSub}>{analytics.leadsInNurture} leads in nurture</Text>
        </View>
        <TouchableOpacity onPress={()=>setShowAddModal(true)} style={ns.headerBtn}><MaterialCommunityIcons name="plus" size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderAnalyticsCards=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={ns.statsRow} contentContainerStyle={{paddingHorizontal:16}}>
      {[{l:'Campaigns',v:analytics.totalCampaigns,c:'#DB2777'},{l:'In Nurture',v:analytics.leadsInNurture,c:'#8B5CF6'},{l:'Emails Sent',v:analytics.emailsSent,c:'#3B82F6'},{l:'Open Rate',v:`${analytics.openRate.toFixed(1)}%`,c:'#10B981'},{l:'Click Rate',v:`${analytics.clickRate.toFixed(1)}%`,c:'#F59E0B'},{l:'Conversion',v:`${analytics.conversionRate.toFixed(1)}%`,c:'#EF4444'}].map((s2,i)=>(
        <View key={i} style={ns.statCard}><Text style={[ns.statValue,{color:s2.c}]}>{s2.v}</Text><Text style={ns.statLabel}>{s2.l}</Text></View>
      ))}
    </ScrollView>
  );

  const renderTabs=()=>(
    <View style={ns.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'campaigns',t:'Campaigns',i:'bullhorn'},{k:'sequences',t:'Sequences',i:'arrow-decision'},{k:'leads',t:'Nurture Leads',i:'account-heart'},{k:'analytics',t:'Performance',i:'chart-bar'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[ns.tab,activeTab===tab.k&&ns.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#DB2777':'#6B7280'}/>
          <Text style={[ns.tabText,activeTab===tab.k&&{color:'#DB2777',fontWeight:'600'}]}>{tab.t}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderCampaigns=()=>campaigns.length===0?(
    <View style={ns.empty}><MaterialCommunityIcons name="bullhorn-outline" size={48} color="#D1D5DB"/><Text style={ns.emptyText}>No nurturing campaigns</Text>
      <TouchableOpacity style={ns.addBtn} onPress={()=>setShowAddModal(true)}><Text style={ns.addBtnText}>+ Create Campaign</Text></TouchableOpacity></View>
  ):(
    <View style={{padding:16}}>{campaigns.map((c,i)=>(
      <TouchableOpacity key={i} style={ns.campaignCard} onPress={()=>{setSelectedCampaign(c);setShowCampaignModal(true);}}>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'flex-start'}}>
          <View style={{flex:1}}>
            <Text style={ns.campName}>{c.name}</Text>
            <Text style={ns.campMeta}>{c.type} · {c.targetStage} stage · {c.leads} leads</Text>
          </View>
          <View style={[ns.statusBadge,{backgroundColor:c.status==='active'?'#ECFDF5':'#F3F4F6'}]}>
            <Text style={[ns.statusText,{color:c.status==='active'?'#059669':'#6B7280'}]}>{c.status}</Text>
          </View>
        </View>
        {c.description?<Text style={ns.campDesc}>{c.description}</Text>:null}
        <View style={ns.campStats}>
          <Text style={ns.campStat}>📧 {c.emailsSent} sent</Text>
          <Text style={ns.campStat}>👁 {c.opens} opens</Text>
          <Text style={ns.campStat}>🖱 {c.clicks} clicks</Text>
          <Text style={ns.campStat}>✅ {c.conversions} conv</Text>
        </View>
      </TouchableOpacity>
    ))}</View>
  );

  const renderSequences=()=>(
    <View style={ns.section}><Text style={ns.sectionTitle}>Nurturing Sequence</Text>
      {sequences.length===0?<Text style={ns.emptyText}>No sequences configured</Text>:
        <View style={ns.timeline}>{sequences.map((seq,i)=>(
          <View key={i} style={ns.timelineItem}>
            <View style={ns.timelineDot}>
              <View style={[ns.dotInner,{backgroundColor:getTypeColor(seq.type)}]}>
                <MaterialCommunityIcons name={getTypeIcon(seq.type)} size={14} color="#FFF"/>
              </View>
              {i<sequences.length-1&&<View style={ns.timelineLine}/>}
            </View>
            <View style={ns.timelineContent}>
              <Text style={ns.seqTitle}>{seq.title}</Text>
              <Text style={ns.seqMeta}>{seq.delay} · {seq.type}</Text>
              <Text style={ns.seqContent}>{seq.content}</Text>
              <View style={[ns.seqStatus,{backgroundColor:seq.status==='sent'?'#ECFDF5':'#EFF6FF'}]}>
                <Text style={[ns.seqStatusText,{color:seq.status==='sent'?'#059669':'#2563EB'}]}>{seq.status}</Text>
              </View>
            </View>
          </View>
        ))}</View>
      }
    </View>
  );

  const renderNurtureLeads=()=>nurturingLeads.length===0?(
    <View style={ns.empty}><MaterialCommunityIcons name="account-heart-outline" size={48} color="#D1D5DB"/><Text style={ns.emptyText}>No leads in nurture</Text></View>
  ):(
    <View style={{padding:16}}>{nurturingLeads.map((l,i)=>(
      <View key={i} style={ns.leadCard}>
        <View style={{flexDirection:'row',alignItems:'center'}}>
          <View style={[ns.engCircle,{borderColor:l.engagementScore>=60?'#10B981':l.engagementScore>=30?'#F59E0B':'#EF4444'}]}>
            <Text style={[ns.engScore,{color:l.engagementScore>=60?'#10B981':l.engagementScore>=30?'#F59E0B':'#EF4444'}]}>{l.engagementScore}</Text>
          </View>
          <View style={{flex:1,marginLeft:10}}>
            <Text style={ns.leadName}>{l.name}</Text>
            <Text style={ns.leadMeta}>{l.company} · Step {l.sequenceStep}/5</Text>
          </View>
          <Text style={ns.lastTouch}>{formatDate(l.lastTouch)}</Text>
        </View>
      </View>
    ))}</View>
  );

  const renderPerformance=()=>(
    <View style={ns.section}><Text style={ns.sectionTitle}>Campaign Performance</Text>
      <View style={ns.perfGrid}>
        {[{l:'Open Rate',v:`${analytics.openRate.toFixed(1)}%`,c:'#10B981',i:'email-open'},{l:'Click Rate',v:`${analytics.clickRate.toFixed(1)}%`,c:'#3B82F6',i:'cursor-default-click'},
          {l:'Conversion',v:`${analytics.conversionRate.toFixed(1)}%`,c:'#8B5CF6',i:'check-decagram'},{l:'Engagement',v:`${analytics.avgEngagement}%`,c:'#F59E0B',i:'heart-pulse'}].map((m,i)=>(
          <View key={i} style={ns.perfCard}>
            <MaterialCommunityIcons name={m.i} size={24} color={m.c}/>
            <Text style={[ns.perfValue,{color:m.c}]}>{m.v}</Text><Text style={ns.perfLabel}>{m.l}</Text>
          </View>
        ))}
      </View>
    </View>
  );

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#DB2777']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){case 'campaigns':return wrap(renderCampaigns());case 'sequences':return wrap(renderSequences());case 'leads':return wrap(renderNurtureLeads());case 'analytics':return wrap(renderPerformance());default:return null;}
  };

  const renderAddModal=()=>(
    <Modal visible={showAddModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAddModal(false)}>
      <View style={ns.modalContainer}>
        <View style={ns.modalHeader}><Text style={ns.modalTitle}>New Nurturing Campaign</Text>
          <TouchableOpacity style={ns.modalClose} onPress={()=>setShowAddModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity></View>
        <ScrollView style={{flex:1,padding:16}}>
          {[{k:'name',l:'Campaign Name *',p:'Welcome Series'},{k:'description',l:'Description',p:'Describe the campaign...',m:true}].map(f=>(
            <View key={f.k} style={ns.formField}><Text style={ns.formLabel}>{f.l}</Text>
              <TextInput style={[ns.formInput,f.m&&{height:80,textAlignVertical:'top'}]} placeholder={f.p} placeholderTextColor="#9CA3AF" value={newCampaign[f.k]} onChangeText={v=>setNewCampaign({...newCampaign,[f.k]:v})} multiline={f.m}/></View>
          ))}
          <View style={ns.formField}><Text style={ns.formLabel}>Type</Text>
            <View style={{flexDirection:'row'}}>
              {['email','call','sms','social'].map(t=>(
                <TouchableOpacity key={t} style={[ns.typeChip,newCampaign.type===t&&{backgroundColor:getTypeColor(t)}]} onPress={()=>setNewCampaign({...newCampaign,type:t})}>
                  <MaterialCommunityIcons name={getTypeIcon(t)} size={14} color={newCampaign.type===t?'#FFF':'#6B7280'}/>
                  <Text style={[ns.typeChipText,newCampaign.type===t&&{color:'#FFF'}]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <View style={ns.formField}><Text style={ns.formLabel}>Target Stage</Text>
            <View style={{flexDirection:'row',flexWrap:'wrap'}}>
              {['lead','contacted','qualified','proposal'].map(st=>(
                <TouchableOpacity key={st} style={[ns.typeChip,newCampaign.targetStage===st&&{backgroundColor:'#DB2777'}]} onPress={()=>setNewCampaign({...newCampaign,targetStage:st})}>
                  <Text style={[ns.typeChipText,newCampaign.targetStage===st&&{color:'#FFF'}]}>{st}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <TouchableOpacity style={ns.createBtn} onPress={createCampaign}>
            <MaterialCommunityIcons name="check" size={20} color="#FFF"/><Text style={ns.createBtnText}>Create Campaign</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    </Modal>
  );

  const renderDetailModal=()=>(
    <Modal visible={showCampaignModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowCampaignModal(false)}>
      <View style={ns.modalContainer}>
        <View style={ns.modalHeader}><Text style={ns.modalTitle}>{selectedCampaign?.name||'Campaign'}</Text>
          <TouchableOpacity style={ns.modalClose} onPress={()=>setShowCampaignModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity></View>
        {selectedCampaign&&<ScrollView style={{flex:1,padding:16}}>
          <View style={ns.mSection}><Text style={ns.mTitle}>Overview</Text>
            <Text style={ns.mText}>Type: {selectedCampaign.type}</Text><Text style={ns.mText}>Status: {selectedCampaign.status}</Text>
            <Text style={ns.mText}>Target: {selectedCampaign.targetStage} stage</Text><Text style={ns.mText}>Leads: {selectedCampaign.leads}</Text>
            {selectedCampaign.description&&<Text style={ns.mText}>{selectedCampaign.description}</Text>}</View>
          <View style={ns.mSection}><Text style={ns.mTitle}>Metrics</Text>
            <Text style={ns.mText}>Emails Sent: {selectedCampaign.emailsSent}</Text><Text style={ns.mText}>Opens: {selectedCampaign.opens} ({selectedCampaign.emailsSent>0?(selectedCampaign.opens/selectedCampaign.emailsSent*100).toFixed(1):0}%)</Text>
            <Text style={ns.mText}>Clicks: {selectedCampaign.clicks}</Text><Text style={ns.mText}>Conversions: {selectedCampaign.conversions}</Text></View>
          {companyProfile&&<View style={ns.mSection}><Text style={ns.mTitle}>Company</Text><Text style={ns.mText}>{companyProfile.name}{companyProfile.industry?` · ${companyProfile.industry}`:''}</Text></View>}
        </ScrollView>}
      </View>
    </Modal>
  );

  if(isLoading)return <SafeAreaView style={ns.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#DB2777"/><Text style={ns.loadingText}>Loading nurturing data...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={ns.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderAnalyticsCards()}{renderTabs()}{renderContent()}{renderAddModal()}{renderDetailModal()}
    </SafeAreaView>
  );
}

const ns=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingText:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},headerBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},headerCenter:{flex:1,marginLeft:12},headerTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},headerSub:{fontSize:13,color:'#FBCFE8',marginTop:2},
  statsRow:{flexGrow:0,flexShrink:0,backgroundColor:'#FFF',paddingVertical:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},statCard:{alignItems:'center',marginRight:16,minWidth:70},statValue:{fontSize:16,fontWeight:'bold'},statLabel:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#FDF2F8'},tabText:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},sectionTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  empty:{alignItems:'center',padding:32},emptyText:{fontSize:16,color:'#6B7280',marginTop:12},addBtn:{backgroundColor:'#DB2777',paddingHorizontal:20,paddingVertical:10,borderRadius:8,marginTop:12},addBtnText:{color:'#FFF',fontWeight:'600'},
  campaignCard:{backgroundColor:'#FFF',marginBottom:8,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},campName:{fontSize:15,fontWeight:'600',color:'#1F2937'},campMeta:{fontSize:12,color:'#6B7280',marginTop:1},campDesc:{fontSize:12,color:'#6B7280',marginTop:4},
  statusBadge:{borderRadius:10,paddingHorizontal:8,paddingVertical:2},statusText:{fontSize:11,fontWeight:'600'},
  campStats:{flexDirection:'row',justifyContent:'space-between',marginTop:8,backgroundColor:'#F9FAFB',borderRadius:6,padding:8},campStat:{fontSize:11,color:'#6B7280'},
  timeline:{paddingLeft:4},timelineItem:{flexDirection:'row',marginBottom:4},timelineDot:{alignItems:'center',width:30},dotInner:{width:26,height:26,borderRadius:13,justifyContent:'center',alignItems:'center'},timelineLine:{width:2,flex:1,backgroundColor:'#E5E7EB',marginTop:4},
  timelineContent:{flex:1,marginLeft:12,paddingBottom:16,borderBottomWidth:1,borderBottomColor:'#F3F4F6'},seqTitle:{fontSize:14,fontWeight:'600',color:'#1F2937'},seqMeta:{fontSize:12,color:'#6B7280',marginTop:1},seqContent:{fontSize:12,color:'#6B7280',marginTop:2},
  seqStatus:{borderRadius:10,paddingHorizontal:8,paddingVertical:2,alignSelf:'flex-start',marginTop:4},seqStatusText:{fontSize:10,fontWeight:'600'},
  leadCard:{backgroundColor:'#FFF',marginBottom:8,padding:12,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},leadName:{fontSize:15,fontWeight:'600',color:'#1F2937'},leadMeta:{fontSize:12,color:'#6B7280',marginTop:1},lastTouch:{fontSize:11,color:'#9CA3AF'},
  engCircle:{width:40,height:40,borderRadius:20,borderWidth:2,justifyContent:'center',alignItems:'center'},engScore:{fontSize:14,fontWeight:'bold'},
  perfGrid:{flexDirection:'row',flexWrap:'wrap',justifyContent:'space-between'},perfCard:{width:(width-64)/2,backgroundColor:'#F9FAFB',borderRadius:10,padding:14,alignItems:'center',marginBottom:8},perfValue:{fontSize:20,fontWeight:'bold',marginTop:4},perfLabel:{fontSize:12,color:'#6B7280',marginTop:4},
  modalContainer:{flex:1,backgroundColor:'#FFF'},modalHeader:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  mSection:{marginBottom:20},mTitle:{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8},mText:{fontSize:14,color:'#6B7280',lineHeight:22},
  formField:{marginBottom:14},formLabel:{fontSize:14,fontWeight:'600',color:'#1F2937',marginBottom:6},formInput:{backgroundColor:'#F3F4F6',borderRadius:8,padding:12,fontSize:14,color:'#1F2937',borderWidth:1,borderColor:'#E5E7EB'},
  typeChip:{flexDirection:'row',alignItems:'center',paddingHorizontal:12,paddingVertical:6,borderRadius:16,backgroundColor:'#F3F4F6',marginRight:6,marginBottom:4},typeChipText:{fontSize:12,color:'#6B7280',marginLeft:4},
  createBtn:{flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#DB2777',borderRadius:10,padding:14,marginTop:8},createBtnText:{color:'#FFF',fontWeight:'700',fontSize:16,marginLeft:8},
});